import { child, onChildAdded, onValue, set, query, orderByKey, startAt } from 'firebase/database';
import _ from 'lodash'
import Swal from 'sweetalert2';
import AdapterMobileV1 from '~/class/AdapterMobileV1';
import { useMobileV1Toast } from '~/composables/useMobileV1Toast';
import { mobileItemIndex } from '~/utils/mobileOrderItemIndex';

export const usePosMobileV1Store = defineStore("mobile-v1", () => {

    const loaded = ref(false)

    const pariedDevices = ref({})


    const tableToUuid = ref({});
    const uuidToTable = ref({});

    const processedUniqueIds = new Set<string>();

    // --- live vs replay -----------------------------------------------------
    // Firebase replays every existing child when a listener attaches, so the first
    // burst after subscribe is history, not new orders. That distinction is
    // load-bearing: the legacy phone gives up 8s after sending
    // (jobMobileOrder.java:170) and tells the employee the order failed so they
    // walk to the cashier. A node handed to us in the replay burst therefore has
    // NOBODY waiting on it — acking it would fire a success dialog and
    // clearAllData() on a phone that moved on long ago (its own staleness check,
    // `diffMs <= 8000`, only rejects FUTURE datetimes, so it does not protect us).
    // Anything after the window is the phone writing right now.
    //
    // Plain Date.now() on BOTH sides on purpose — this measures elapsed time
    // against our own clock. useServerTime().nowMs() would skew the delta if
    // offsetMs happens to resolve in between. And never use order.datetime: that
    // is the phone's clock, which is exactly what we cannot trust.
    let subscribedAt = 0;
    const REPLAY_WINDOW_MS = 5000;
    const isReplayDelivery = () => Date.now() - subscribedAt < REPLAY_WINDOW_MS;

    // Table alias as the jobchain keys it. Must mirror JobBuilderV1.withTableAlias
    // EXACTLY — it is what produced every `job.alias` and every `tables` key we
    // compare against. Deliberately does not .trim(): the builder doesn't either,
    // so trimming here would make a padded alias miss its own job and let a
    // duplicate through.
    const normaliseAlias = (rawAlias: any) =>
        String(rawAlias || '').replace('โต๊ะ ', 'T').replace('Table ', 'T');

    // The jobchain key this order WOULD have. AdapterMobileV1 builds
    // jobId = `${uniqueid}__${alias}` and pos.jobs.add() keys the chain by
    // jobId.replaceAll('.', '_') — so this reproduces that exact string and the
    // "have I taken this order?" question becomes a single lookup. No scan.
    const jobchainKeyFor = (uniqueid: string, rawAlias: any) =>
        `${uniqueid}__${normaliseAlias(rawAlias)}`.replaceAll('.', '_');

    // Has this mobile order already been taken? processedUniqueIds only knows about
    // THIS page session, and a reload wipes it — which is how one order became four
    // kitchen slips: each reload re-ingested one more un-acked retry. The jobchain
    // is RTDB-backed, restored at boot and cleared on Close Shop, so it remembers
    // across reloads. Its key already encodes uniqueid AND table, so a retry finds
    // its own job directly.
    //
    // Requires the jobchain to be loaded before we subscribe — see init().
    const isAlreadyHandled = (uniqueid: string, rawAlias: any) =>
        processedUniqueIds.has(uniqueid) ||
        !!(usePosJobsStore().jobs as any)[jobchainKeyFor(uniqueid, rawAlias)];

    // The dedupe above is only as good as the jobchain behind it: subscribe before
    // it has loaded and EVERY replayed node looks brand new — a duplicate job and a
    // duplicate kitchen slip for each. bootstrap awaits Layer B (jobs) before
    // initByRunMode today (bootstrap.ts:204 → :206), so this resolves immediately;
    // it exists so a future re-order of bootstrap stalls loudly instead of silently
    // reprinting the kitchen. Waiting forever beats starting early: no mobile orders
    // is a failure the staff see at once, duplicate slips are one nobody catches.
    //
    // This CANNOT hang on a brand-new shop whose jobchain is still empty, and that
    // rests on two properties of jobs.bootOnline → waitForFirstRTDBSettle:
    //   1. it listens with onValue, which fires even for a node that doesn't exist
    //      (snapshot val() === null) — unlike onChildAdded, which needs a child;
    //   2. it also races a 3s timeout, so `loaded` flips even if RTDB never answers.
    // Swap that helper to onChildAdded and an empty shop would wait here forever.
    const waitForJobchainLoaded = () => {
        const jobsStore = usePosJobsStore();
        if (jobsStore.loaded) return Promise.resolve();
        console.warn('[mobile-v1] jobchain not loaded yet — holding mobile ingest until it is');
        return new Promise<void>((resolve) => {
            const stop = watch(() => jobsStore.loaded, (isLoaded) => {
                if (!isLoaded) return;
                stop();
                resolve();
            });
        });
    };

    // A duplicate needs two different answers depending on whether anyone is still
    // listening. Getting this wrong in either direction causes a real incident:
    //   live  → the phone is inside its 8s window. Ack read = 2 (its ONLY success
    //           signal, jobMobileOrder.java:287) so it stops retrying. KEEP the
    //           node: it IS the phone's History record (HistoryFragmentV2.java:306
    //           renders read == 2), read == 2 already disarms it for good via the
    //           `order.read == 2` guard below, and deleting would race
    //           registerCallback's `if (value != null)` — handing it a null means
    //           timeout, means yet another retry.
    //   replay → the phone timed out ages ago and the employee already told the
    //           cashier. Acking now would pop a stale "success" and wipe the cart
    //           they are ringing up at this moment. Drop the node instead: the
    //           phone ignores null, so this is silent, and the order is already in
    //           the jobchain anyway.
    const resolveDuplicate = (refPath: any, order: any) => {
        if (isReplayDelivery()) {
            set(refPath, null);
            return;
        }
        // echo the order back untouched apart from `read` — the phone checks
        // `datetime` against its own clock, so rewriting it would fail the ack.
        order.read = 2;
        set(refPath, JSON.stringify(order));
    };

    const convertTableToUuid = (table: string) => {
        return tableToUuid.value[table] || null;
    }

    const convertUuidToTable = (uuid: string) => {
        return uuidToTable.value[uuid] || null;
    }

    const generateCustomerArray = () => {

        const zoneList = usePosTableListStore().zoneList;
        const jobStore = usePosJobsStore();
        const stateStore = usePosTableStatesStore();

        const tablesData = jobStore.tables;
        const tableState = stateStore.tablesState;

        let res: number[] = [];
        _.forEach(zoneList, (zone) => {
            _.times(zone.tableCount, (index) => {
                let tableLabel = `${zone.name}${index + 1}`;
                let items = tablesData[tableLabel]?.items || [];
                let isLock = tableState[tableLabel]?.lock || false;

                let tableCode = isLock ? 99 : items.length > 0 ? 2 : 0;
                res.push(tableCode);
            });
        });

        return res;
    }


    const handleMobileV1 = (snapshot: any) => {
        // Cashier-originated history rows are push()-keyed ("-…"); phone orders
        // are epoch-millis-keyed. A push key here can only be our own
        // writeHistoryRecord leaking past the startAt() key ordering — never
        // ingest it (belt: those records also carry read: 2).
        if (String(snapshot.key || '').startsWith('-')) {
            return;
        }

        const refPath = snapshot.ref;
        var jsonString = snapshot.val();

        var order = JSON.parse(jsonString);
        // console.log(order);
        if (order.void || order.read == 2) {
            return;
        }

        // Duplicate — the phone re-sent an order we already took. It only re-sends
        // because it never saw an ack, and this branch used to `return` silently:
        // no read = 2, no delete. That guaranteed the phone's 8s timeout (so the
        // employee hit Retry, producing ANOTHER duplicate) and left the node
        // read != 2 — still eligible for ingest on the next reload. One order, four
        // kitchen slips, one per reload. Answer the phone instead.
        if (order.uniqueid && isAlreadyHandled(order.uniqueid, order.alias)) {
            resolveDuplicate(refPath, order);
            return;
        }

        // Block incoming mobile orders while the shop is running over a closed
        // business day. Same guard the cashier hits on the main POS terminal
        // (pos.tableInstance.ts → addRequest): until the shop is closed and
        // reopened for the current business date, no new order should be
        // accepted from any channel. Reject the order the same way an unpaired
        // device is handled — remove it from RTDB and warn — so it never becomes
        // a jobchain and the order doesn't sit waiting on an unread record.
        if (usePosJobsStore().isRunningOverClosedDay()) {
            setTimeout(() => {
                set(refPath, null);
            }, 2500);

            useMobileV1Toast().push({
                variant: 'warn',
                text: 'ร้านเปิดข้ามวัน — กรุณาปิดร้านแล้วเปิดใหม่ก่อนรับออเดอร์จากมือถือ',
                duration: 4000,
            });
            return;
        }

        // un paired device
        if (!pariedDevices.value[order.deviceId]) {

            setTimeout(() => {
                set(refPath, null);
            }, 2500);


            useMobileV1Toast().push({
                variant: 'warn',
                text: `ออเดอร์จากอุปกรณ์ที่ยังไม่ได้จับคู่ — ${order.deviceId}`,
                duration: 3000,
            });
            return;
        }

        // Table lock guard — REJECT (do not queue, do not retry). If the cashier
        // is currently holding/editing this table (locked), an incoming mobile
        // order must NOT be merged: doing so overwrites the cashier's in-progress
        // state. Reject it the same way an unpaired-device / closed-day order is
        // handled — remove it from RTDB and warn — so it does NOT sit waiting and
        // does NOT auto-enter later. The employee must re-submit the order once
        // the table is unlocked. We only have the raw order.alias ("โต๊ะ 7" /
        // "Table 7") here — normalize it to the "T7" form the adapter /
        // JobBuilderV1 (withTableAlias) and the lock key space use.
        const lockAlias = String(order.alias || '').replace('โต๊ะ ', 'T').replace('Table ', 'T');
        if (usePosTableStatesStore().isLockedWithTable(lockAlias)) {
            setTimeout(() => {
                set(refPath, null);
            }, 2500);

            useMobileV1Toast().push({
                variant: 'warn',
                text: `โต๊ะ ${lockAlias} กำลังถูกแก้ไข — ออเดอร์ถูกยกเลิก กรุณาส่งใหม่อีกครั้งหลังปลดล็อก`,
                duration: 3000,
            });
            return;
        }

        if (order.uniqueid) processedUniqueIds.add(order.uniqueid);

        // Item-level dedupe — ตัด item ที่เคยรับเข้าระบบแล้ว
        // key = `${alias}_${item.uniqueid}` กัน timestamp ชนข้ามโต๊ะ, looked up in
        // the WHOLE day's jobchain (see utils/mobileOrderItemIndex). It used to
        // check only lines still on the table, so a retry landing after payment
        // passed and reopened the paid bill (5224 / T22).
        const originalItems = Array.isArray(order.orderitems) ? order.orderitems : [];
        const { fresh: filteredItems } = mobileItemIndex.partition(
            usePosJobsStore().jobs as any, order.alias, originalItems);

        if (filteredItems.length === 0) {
            resolveDuplicate(refPath, order);
            useMobileV1Toast().push({
                variant: 'warn',
                text: `ออเดอร์ซ้ำ — ${order.alias || ''}`,
                duration: 2500,
            });
            return;
        }

        order.orderitems = filteredItems;
        order.read = 2;
        const filteredJsonString = JSON.stringify(order);
        set(refPath, filteredJsonString);

        const job = new AdapterMobileV1().fromCloudToJobchain(filteredJsonString);

        // Legacy mobile can't be trusted for the guest count OR the "opening
        // table" flag: it sends the real guest number only when the table is
        // FIRST opened, then a stale "2" on every later order for that table.
        // Reconcile BOTH against POS state before the job is added — the table's
        // own item count is the source of truth, not the phone. Key by job.alias,
        // the same key the adapter and replay use.
        //   - new table (no items yet) → keep the mobile guest number, and stamp
        //     isOpeningTable = true so downstream can compute per-open values
        //     (guest count, open state, etc) off a reliable flag.
        //   - re-add (table already has items) → force guests 0 (kitchen print
        //     falls back to the table's real count in TableStates → jobs.tables
        //     instead of the phone's duplicate "2") and isOpeningTable = false.
        // isOpeningTable lives top-level on the job, matching withIsOpeningTable
        // used by the cart and the mobile adapter.
        // Defensive: never let this throw and block an incoming order.
        try {
            const existingItems = usePosJobsStore().tables?.[job.alias]?.items?.length || 0;
            const isNewTable = existingItems === 0;
            job.isOpeningTable = isNewTable;
            if (!isNewTable) {
                job.guests = 0;
            }
        } catch (e) {
            console.warn('mobile guest/opening reconcile failed, leaving job as-is', e);
        }

        // Stamp the PC receive time. Mobile order `timestamp` is the phone's clock
        // and may be wrong; pcTimestamp uses the same server-synced clock the
        // cashier stamps its own orders with, so the jobchain replays in true
        // arrival order regardless of the phone's clock (see computeJobchain).
        job.pcTimestamp = useServerTime().nowISO();
        usePosJobsStore().add(job, true);

        useMobileV1Toast().push({
            variant: 'success',
            text: `New order from — ${job.alias}`,
            duration: 2000,
        });
    };

    const autoResponseHandshake = (snapshot: any, childKey: any) => {
        if (snapshot.val() == 1) {
            // get current path ref
            const refPath = snapshot.ref;

            set(refPath, 2);
            // 8s cleanup, mirroring every sibling handshake (job-gateway /
            // void-v1 @8s, terminal / mobile-v2 @2s) — matches the phone's own
            // wait window (jobMobileOrder.java: 8s, 15s on retry), and its
            // value listener ignores null so the delete is invisible to it.
            // Without this the node grew one permanent child per ping all day,
            // and a phone reusing a stable key produced child_changed events
            // this onChildAdded never saw — the phone then retried `1` forever,
            // unanswered. Deleting fixes that too: re-creating a removed key
            // fires onChildAdded again.
            setTimeout(() => {
                set(refPath, null);
            }, 8000);
        }
    };

    // How far back to replay history children at boot — recovers orders that landed
    // while the cashier PC was down. Kept at 1h deliberately. Widening it is
    // tempting (a phone whose clock runs slow writes keys below the cut-off and its
    // orders are dropped silently, never ingested and never deleted) but it fights
    // the phone's own contract: an order that failed hours ago was already reported
    // to the cashier by the employee, and resurrecting it into the kitchen now
    // would cook food nobody is waiting for. Fix the clock source, not the width.
    const REPLAY_LOOKBACK_MS = 60 * 60 * 1000;

    const watchFromMobileV1 = () => {
        const handshakeRef = useRealtimeDatabase().handshakeMobileV1Ref();

        onChildAdded(handshakeRef, autoResponseHandshake);

        // REALTIME WATCH
        const zoneList = usePosTableListStore().zoneList;
        const tablesList = zoneList.map((zone) => zone.tableCount);
        const totalTable = _.sum(tablesList);

        // Keys are epoch-millis stamped by the PHONE, so anchor the cut-off to the
        // server-synced clock instead of this PC's raw Date.now() — a drifting
        // cashier clock would otherwise move the window under our feet.
        const epoch = (useServerTime().nowMs() - REPLAY_LOOKBACK_MS).toString();

        // Stamp BEFORE attaching: everything Firebase hands back in the burst that
        // follows is pre-existing history, not a live order (see isReplayDelivery).
        subscribedAt = Date.now();

        for (let i = 1; i <= totalTable; i++) {
            const path = `tables/${i}/history`;
            const root_ref = useRealtimeDatabase().customerRootRef();
            const ref = child(root_ref, path);
            const q = query(ref, orderByKey(), startAt(epoch));
            onChildAdded(q, handleMobileV1);
        }

    };

    // Resolves on the FIRST `paired` snapshot so init() can await it before
    // attaching watchFromMobileV1: the history replay burst used to race an
    // empty pariedDevices map, sending every un-acked replayed order into the
    // "unpaired device" branch — toast + delete of durable history children.
    // Bounded at 3s (waitForFirstRTDBSettle discipline) so a dead network
    // can't hang cashier boot; the onValue subscription stays live for
    // ongoing pairing updates.
    const fetchPairDevices = () => new Promise<void>((resolve) => {
        const root_ref = useRealtimeDatabase().customerRootRef()
        const paired_ref = child(root_ref, "paired");
        const timer = setTimeout(resolve, 3000);
        onValue(paired_ref, (snapshot) => {
            const pairDevices = snapshot.val() || {};
            const map = {}
            for (const key in pairDevices) {
                const { deviceId, deviceName } = pairDevices[key]
                map[deviceId] = true
            }
            pariedDevices.value = map;
            clearTimeout(timer);
            resolve();
        });
    })

    // Build the table-alias ↔ legacy-uuid lookup. This map is the gate for
    // ALL per-table mobile writes: convertTableToUuid() returns null until it
    // is built, and both AdapterMobileHistoryV1.fromJobToHistory (history) and
    // updateTableToMobileV1Cloud (table detail / void lines) bail out / write
    // to a junk path on a null uuid. Pure data, no RTDB listeners.
    const buildTableUuidMap = () => {
        const zoneList = usePosTableListStore().zoneList;
        let count = 1
        _.forEach(zoneList, (zone) => {
            _.times(zone.tableCount, (index) => {
                let tableLabel = `${zone.name}${index + 1}`;
                tableToUuid.value[tableLabel] = count;
                uuidToTable.value[count] = tableLabel;
                count++;
            });
        });
    };

    // Second cashier: build ONLY the uuid map so this device publishes its own
    // adds to the legacy mobile cloud — history (tables/{uuid}/history), job
    // detail (tables/{uuid}/list) and the count list (tables/customercounts) —
    // via the global mobile-v1.sync plugin's $onAction("add") hook. The plugin
    // already runs on the second cashier; this map is the missing key that
    // makes convertTableToUuid() resolve instead of returning null.
    //
    // Deliberately excludes the receive-side (watchFromMobileV1 / handshake /
    // fetchPairDevices). That ingests incoming mobile orders into the jobchain
    // and MUST stay on the main cashier only; running it here too would add
    // every mobile order twice (duplicate jobs).
    const initPublishOnly = () => {
        buildTableUuidMap()
    };

    const init = async () => {
        buildTableUuidMap()

        // Order matters: the jobchain IS the duplicate guard, so it must be loaded
        // before a single history child can reach handleMobileV1.
        await waitForJobchainLoaded()

        const handshakeRef = useRealtimeDatabase().handshakeMobileV1Ref();
        set(handshakeRef, null);

        // Await the first paired snapshot BEFORE subscribing to history — same
        // ordering discipline as waitForJobchainLoaded above.
        await fetchPairDevices()
        watchFromMobileV1()
    };



    return {
        convertTableToUuid,
        convertUuidToTable,
        generateCustomerArray,
        init,
        initPublishOnly,
        tableToUuid,
        uuidToTable,
        pariedDevices
    }
})